import React, { useEffect, useMemo, useState } from 'react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import MarkdownLite from '../MarkdownLite';
import { ChevronLeft, ChevronRight, Sparkles, PartyPopper, AlertTriangle, Lightbulb, Smile } from 'lucide-react';

/**
 * BoardMeetingModal — annual "Comité de direction" festive moment.
 *
 * At year-end, each hired support role can push a Messagerie message. We
 * aggregate them into a single ceremonial slideshow : Nathalie opens, each
 * character has their slide, and the patron closes with a single CTA.
 *
 * Reads `pendingBoardMeeting` from the store and dismisses via
 * `dismissBoardMeeting`. The individual messages also live in the inbox so
 * they remain accessible after the ceremony.
 *
 * 31/05/2026 — narrative bonus follow-up to the support yearly digest.
 */

const TONE_STYLE = {
  celebratory: {
    icon: PartyPopper,
    label: 'Bonne nouvelle',
    border: 'border-emerald-500/40',
    accentText: 'text-emerald-300',
    accentTextDim: 'text-emerald-300/80',
    accentBorder: 'border-emerald-500/50',
    dotActive: 'bg-emerald-400',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
  },
  warning: {
    icon: AlertTriangle,
    label: 'Vigilance',
    border: 'border-amber-500/40',
    accentText: 'text-amber-300',
    accentTextDim: 'text-amber-300/80',
    accentBorder: 'border-amber-500/50',
    dotActive: 'bg-amber-400',
    btn: 'bg-amber-600 hover:bg-amber-700',
  },
  advice: {
    icon: Lightbulb,
    label: 'Conseil',
    border: 'border-sky-500/40',
    accentText: 'text-sky-300',
    accentTextDim: 'text-sky-300/80',
    accentBorder: 'border-sky-500/50',
    dotActive: 'bg-sky-400',
    btn: 'bg-sky-600 hover:bg-sky-700',
  },
  friendly: {
    icon: Smile,
    label: 'Synthèse',
    border: 'border-violet-500/40',
    accentText: 'text-violet-300',
    accentTextDim: 'text-violet-300/80',
    accentBorder: 'border-violet-500/50',
    dotActive: 'bg-violet-400',
    btn: 'bg-violet-600 hover:bg-violet-700',
  },
};

const PORTRAIT_BASE = '/support-portraits';

// Ensure Nathalie speaks first (she's the assistante administrative — she
// kicks off the meeting). Bruno closes (atelier). Others ordered by family
// to keep a coherent flow : commerce → marketing → comm → achats → finance
// → RH → SAV → garage.
const ROLE_DISPLAY_ORDER = [
  'role-nathalie-robert',
  'role-christophe-faure',
  'role-lea-bertrand',
  'role-marc-dupuis',
  'role-sylvie-marchand',
  'role-yanis-elkaroui',
  'role-sherazade-fetitou',
  'role-thomas-vidal',
  'role-aicha-diallo',
  'role-karim-benali',
  'role-helene-pichon',
  'role-sophie-lambert',
  'role-pierre-marchetti',
  'role-nadia-cheikh',
  'role-karim-sefrioui',
  'role-lucas-mercier',
  'role-ines-bouchard',
  'role-mehdi-ferhat',
  'role-bruno-lavergne',
];

export default function BoardMeetingModal() {
  const { t } = useTranslation();
  const pending = useGameStore(s => s.pendingBoardMeeting);
  const dismiss = useGameStore(s => s.dismissBoardMeeting);
  const [reveal, setReveal] = useState(false);
  const [step, setStep] = useState(0);

  // Sort messages by role display order — Nathalie first, others by family.
  const messages = useMemo(() => {
    if (!pending || !Array.isArray(pending.messages)) return [];
    const ordered = ROLE_DISPLAY_ORDER
      .map(roleId => pending.messages.find(m => m.senderRole === roleId))
      .filter(Boolean);
    // Any unmapped role (future-proofing) appended at the end.
    const known = new Set(ROLE_DISPLAY_ORDER);
    const extras = pending.messages.filter(m => !known.has(m.senderRole));
    return [...ordered, ...extras];
  }, [pending]);

  useEffect(() => {
    if (pending) {
      setReveal(false);
      setStep(0);
      const id = setTimeout(() => setReveal(true), 80);
      return () => clearTimeout(id);
    }
  }, [pending]);

  if (!pending || messages.length === 0) return null;

  const current = messages[step];
  const total = messages.length;
  const isLast = step === total - 1;
  const tone = TONE_STYLE[current.tone] || TONE_STYLE.advice;
  const ToneIcon = tone.icon;

  const next = () => {
    if (isLast) {
      dismiss();
    } else {
      setStep(s => Math.min(s + 1, total - 1));
    }
  };
  const prev = () => setStep(s => Math.max(0, s - 1));

  // Extract sender display name (drop the trailing parenthetical role).
  const senderTitle = (current.senderName || '').replace(/\s*\(.*?\)\s*$/, '');
  const senderRole = (current.senderName || '').match(/\(([^)]+)\)/)?.[1] || '';

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      data-testid="board-meeting-overlay"
    >
      <div
        className={`bg-ink-800 rounded-2xl w-full max-w-2xl max-h-[92vh] shadow-2xl overflow-hidden border-2 ${tone.border} flex flex-col transform transition-all duration-500 ${
          reveal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        data-testid="board-meeting-modal"
      >
        {/* Header — ceremony banner */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 pt-6 pb-5 text-center relative overflow-hidden border-b border-white/5 shrink-0">
          {[0, 1, 2, 3, 4].map(i => (
            <Sparkles
              key={`spark-${i}`}
              className="absolute text-amber-200/30 w-4 h-4"
              style={{
                top: `${15 + (i * 14) % 60}%`,
                left: `${8 + (i * 19) % 80}%`,
                animation: `pulse 3.${i}s ease-in-out infinite`,
                animationDelay: `${i * 0.22}s`,
              }}
            />
          ))}
          <p className="text-amber-200/80 text-[10px] uppercase tracking-[0.2em] font-mono font-bold mb-1.5 relative z-10">
            {t('board_meeting_kicker', { year: pending.year })}
          </p>
          <h2 className="text-2xl font-display font-black text-white relative z-10">
            {t('board_meeting_title')}
          </h2>
          <p className="text-xs text-slate-400 mt-1.5 relative z-10">
            {t('board_meeting_progress', { current: step + 1, total })}
          </p>
        </div>

        {/* Body — current speaker slide. Scrollable container so the
            whole speaker slide (portrait + intro + long memo) never gets
            clipped on small laptops (02/06/2026 fix : Mac 1280×800). */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0" data-testid="board-meeting-modal-body">
          <div className="flex items-start gap-4 mb-4">
            <img
              src={`${PORTRAIT_BASE}/${current.senderRole}.webp`}
              alt={senderTitle}
              className={`w-16 h-16 rounded-xl object-cover border-2 ${tone.accentBorder} flex-shrink-0`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              data-testid="board-meeting-portrait"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-widest font-mono font-bold ${tone.accentText} inline-flex items-center gap-1`}>
                  <ToneIcon className="w-3.5 h-3.5" />
                  {tone.label}
                </span>
              </div>
              <h3 className="text-base font-display font-bold text-white truncate" data-testid="board-meeting-speaker">
                {senderTitle}
              </h3>
              {senderRole && (
                <p className="text-xs text-slate-400 italic truncate">{senderRole}</p>
              )}
            </div>
          </div>

          <div className="rounded-xl bg-slate-900/60 border border-white/5 p-4 mb-4">
            <p className={`text-[11px] uppercase tracking-widest font-mono font-bold ${tone.accentTextDim} mb-2`}>
              {current.subject}
            </p>
            <div
              className="text-sm text-slate-200 leading-relaxed"
              data-testid="board-meeting-body"
            >
              <MarkdownLite text={current.body} />
            </div>
          </div>

          {/* Step navigation dots */}
          <div className="flex items-center justify-center gap-1.5 mb-4" data-testid="board-meeting-dots">
            {messages.map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? `${tone.dotActive} w-6`
                    : i < step
                      ? 'bg-white/30 w-1.5 hover:bg-white/50'
                      : 'bg-white/10 w-1.5 hover:bg-white/30'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Footer — prev / next */}
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={step === 0}
              className="flex items-center gap-1 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-slate-200 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="board-meeting-prev"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('board_meeting_prev')}
            </button>
            <button
              onClick={next}
              className={`flex-1 flex items-center justify-center gap-2 ${tone.btn} text-white py-2.5 rounded-xl text-sm font-bold transition-colors active:scale-[0.98]`}
              data-testid="board-meeting-next"
            >
              {isLast ? t('board_meeting_close') : t('board_meeting_next')}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
